// FormLayoutEditor.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Utilities
import { buildLayoutEditorDocument } from 'pages/layout/layoutPageUtils';

// Services
import {
  buildDefaultFormLayoutScaffold,
  FORM_FIELD_BLOCK_TYPE,
  getVisibleFormFieldKeys,
} from 'services/forms/FormModel';
import { saveFormDocument } from 'services/forms/FormWriteService';
import { isSystemTypeForm, saveTypeForm } from 'services/forms/TypeFormService';

// Components
import LayoutEditor from 'page/LayoutEditor';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * FormLayoutEditor
 *
 * Embeds LayoutEditor for a type-scoped form template.
 */
const FormLayoutEditor = ({
  fieldBlockTypeDefOverride = null,
  fieldSelectorLockedFieldKeys = null,
  formDoc = null,
  getTypeOverride = null,
  inspectorPreview = null,
  onDirtyChange = null,
  onPersistDocument = null,
  previewNode = null,
  showLayoutInspector = false,
  useBlockPlaceholders = false,
}) => {
  // ----- CONTEXT ----- //

  const { getType, getLabelPlural } = useNodeTypes();
  const { user } = useUser();

  const resolveType = getTypeOverride || getType;
  const fieldBlockTypeKey = normalizeString(formDoc?.typeKey || previewNode?.type);
  const typeLabelPlural = getLabelPlural(fieldBlockTypeKey, undefined, { fallback: 'records' }) || 'records';

  // ----- MEMOS ----- //

  const hostTypeDef = useMemo(() => (
    fieldBlockTypeDefOverride || resolveType(fieldBlockTypeKey) || null
  ), [fieldBlockTypeDefOverride, fieldBlockTypeKey, resolveType]);

  const lockedFieldKeys = useMemo(() => (
    Array.isArray(fieldSelectorLockedFieldKeys) ? fieldSelectorLockedFieldKeys : []
  ), [fieldSelectorLockedFieldKeys]);

  const editorDocument = useMemo(() => {
    if (!formDoc) return null;

    const fieldKeys = getVisibleFormFieldKeys(hostTypeDef);

    if (formDoc?.isDefault && !formDoc?.layoutEdited) {
      return buildLayoutEditorDocument({
        ...formDoc,
        ...buildDefaultFormLayoutScaffold({ fieldKeys }),
      });
    }

    const rootNodeKey = normalizeString(formDoc?.layout?.rootNodeKey) || 'layout_root';
    const rootChildren = formDoc?.layout?.nodes?.[rootNodeKey]?.children;
    const hasLayoutScaffold = Boolean(
      formDoc?.layout?.nodes
      && Array.isArray(rootChildren)
      && rootChildren.length,
    );

    if (hasLayoutScaffold) {
      return buildLayoutEditorDocument(formDoc);
    }

    return buildLayoutEditorDocument({
      ...formDoc,
      ...buildDefaultFormLayoutScaffold({ fieldKeys }),
    });
  }, [formDoc, hostTypeDef]);

  // ----- HANDLERS ----- //

  const handlePersistDocument = useCallback(async (nextLayout, nextBlocksByKey) => {
    if (!formDoc?.key) return;

    const draftDoc = {
      ...formDoc,
      blocksByKey: nextBlocksByKey,
      layout: nextLayout,
      layoutEdited: true,
    };

    if (typeof onPersistDocument === 'function') {
      await onPersistDocument(nextLayout, nextBlocksByKey);
      return;
    }

    if (isSystemTypeForm(formDoc)) {
      const typeKey = normalizeString(formDoc?.typeKey);
      if (!typeKey) {
        throw new Error('System form is missing typeKey.');
      }
      await saveTypeForm(typeKey, draftDoc);
      return;
    }

    await saveFormDocument(draftDoc, {
      userKey: user?.key,
    });
  }, [formDoc, onPersistDocument, user?.key]);

  if (!editorDocument) {
    return null;
  }

  // ----- RENDER ----- //

  return (
    <LayoutEditor
      allowContentZoneBlocks={false}
      allowFieldBlocks={true}
      allowNodeBlocks={false}
      allowVideoBlocks={false}
      editorDocument={editorDocument}
      editorIntroHint="Form templates control which fields appear on the Forms tab for every record of this type. Add field blocks to place metadata in the layout."
      editorEmptyStateMessage="No layout rows yet. Add a row, then add a field block to begin."
      fieldBlockType={FORM_FIELD_BLOCK_TYPE}
      fieldBlockTypeDefOverride={hostTypeDef}
      fieldBlockTypeKey={fieldBlockTypeKey}
      fieldSelectorLockedFieldKeys={lockedFieldKeys}
      getTypeOverride={resolveType}
      hideSharedToolbar={false}
      inlineCreateRichTextBlocks={false}
      inspectorPreview={inspectorPreview}
      layoutKey={formDoc?.key || ''}
      pageViewAuthoringEnabled={false}
      previewNode={previewNode}
      richTextDocumentLabel="form"
      showLayoutInspector={showLayoutInspector}
      typeLabelPlural={typeLabelPlural}
      untitledTitle="Untitled Form"
      useBlockPlaceholders={useBlockPlaceholders}
      onDirtyChange={onDirtyChange}
      onPersistDocument={handlePersistDocument}
    />
  );
};

// Dev Tools
FormLayoutEditor.displayName = 'FormLayoutEditor';

export default FormLayoutEditor;
